"use client";

import { AudioVisualizer } from "@/components/audio-visualizer";
import {
  AvatarSize,
  AvatarUser,
  UserAvatar,
} from "@/components/user/user-avatar";
import { ChatRoomSocketQuery } from "@/hooks/queries/chat/room/use-chat-room-socket-query";
import { useMultiBandTrackVolume } from "@/hooks/use-track-volume";
import { cn } from "@/lib/utils";
import { useCallContext } from "@/providers/call-provider";
import { PropsWithClassName } from "@/types";
import { getParticipantMetadata } from "@/utils/chat/get-participant-metadata";
import {
  useConnectionState,
  useSpeakingParticipants,
} from "@livekit/components-react";
import { ConnectionState, Participant, Track } from "livekit-client";
import { motion } from "motion/react";
import React, { PropsWithChildren } from "react";

function limitActiveSpeakers(activeSpeakers: Participant[], limit = 3) {
  if (activeSpeakers.length <= limit) {
    return activeSpeakers;
  }
  return activeSpeakers.slice(0, limit);
}

interface Props {
  readonly showAudioVisualizer?: boolean;
  readonly avatarSize?: AvatarSize;
}

export function ChatRoomActiveSpeakers(
  props: PropsWithChildren<PropsWithClassName<Props>>,
) {
  const call = useCallContext();
  ChatRoomSocketQuery.useSocketQuery(call.channelId.value ?? "");
  const activeSpeakers = useSpeakingParticipants();
  const connectionState = useConnectionState();
  if (connectionState !== ConnectionState.Connected) {
    return null;
  }

  return (
    <>
      <motion.div
        layout
        className={cn(
          "flex h-auto w-auto flex-row items-center justify-center space-x-2",
          props.className,
        )}
      >
        {limitActiveSpeakers(activeSpeakers).map((participant) => {
          const track = participant.getTrackPublications()[0].track;
          const metadata = getParticipantMetadata(participant.metadata);
          if (!track || !metadata) {
            return null;
          }

          return (
            <ActiveSpeakerMotion
              key={participant.sid}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              track={track}
              user={{
                id: metadata.userId,
                name: participant.identity,
                image: metadata.image,
              }}
              showVisualizer={props.showAudioVisualizer}
              avatarSize={props.avatarSize}
            />
          );
        })}
      </motion.div>
      {props.children}
    </>
  );
}

interface ActiveSpeakerProps {
  readonly track?: Track;
  readonly user: AvatarUser;
  readonly showVisualizer?: boolean;
  readonly avatarSize?: AvatarSize;
}

function ActiveSpeaker(
  props: ActiveSpeakerProps,

  _ref: React.Ref<HTMLDivElement>,
) {
  const localMultiBandVolume = useMultiBandTrackVolume(props.track, 7);

  return (
    <div className="flex h-auto w-auto flex-row items-center justify-center space-x-2">
      <UserAvatar
        user={props.user}
        className={cn(
          "pulse-border animate-pulse",
          localMultiBandVolume.at(0)?.at(0)
            ? "pulse-border border"
            : "border-transparent",
        )}
        size={props.avatarSize}
      />
      {props.showVisualizer ? (
        <AudioVisualizer
          state="speaking"
          barWidth={4}
          minBarHeight={2}
          maxBarHeight={50}
          thinkingColor="bg-primary"
          thinkingColorShadow="shadow-lg-primary"
          frequencyColor="bg-warning"
          frequencies={localMultiBandVolume}
          borderRadius={2}
          gap={4}
        />
      ) : null}
    </div>
  );
}

const ActiveSpeakerMotion = motion.create(React.forwardRef(ActiveSpeaker));
